import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Link } from '@/components/LocalizedLink';
import { useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import SEOHead from '@/components/SEOHead';
import FloatingAsset from '@/components/FloatingAsset';
import { fetchBlogPostBySlug } from '@/lib/contentService';
import assetDiamond from '@/assets/asset-chrome-diamond.webp';
import { useLanguage } from '@/contexts/LanguageContext';
import { isMeaningfulBlogContent, resolveLocalizedValue } from '@/lib/blogLocalization';
import { SITE_URL, buildAbsoluteUrl } from '@/lib/seo';
import { templateOgImagePath } from '@/lib/ogTemplateImage.shared.js';
import { localizePath } from '@/lib/localeRouting';
import { getBlogLogicalPath, getBlogPublicSlug, getBlogTagLabel, getBlogTagLinkPath, uniqueBlogTags } from '@/lib/blogSlugs';
import { getAuthorProfilePath, resolveAuthorProfile } from '@/lib/authorCatalog';
import { getBlogCoverImage } from '@/lib/blogCovers';
import {
  buildBreadcrumbSchema,
  buildCollectionItemListSchema,
  buildFaqSchema,
  buildBlogPostingSchema,
  buildAuthorGraphNodes,
  buildSpeakableSchema,
} from '@/lib/schema';
import { getBlogConversionContext } from '@/lib/blogConversion';
import { persistBlogLeadSlug } from '@/lib/leadAttribution';
import { getAuthorityAssetsBySlugs } from '@/lib/authorityAssetCatalog';
import { getRelatedServiceSlugsForBlog, getServicesBySlugs } from '@/lib/serviceCatalog';
import RelatedServicesBlock from '@/components/knowledge/RelatedServicesBlock';
import RelatedPlaybooksBlock from '@/components/knowledge/RelatedPlaybooksBlock';
import FAQSection from '@/components/knowledge/FAQSection';
import BlogConversionPanel from '@/components/blog/BlogConversionPanel';
import BlogLeadMagnet from '@/components/blog/BlogLeadMagnet';
import DirectAnswerBlock from '@/components/knowledge/DirectAnswerBlock';
import { ArticleShareBar } from '@/components/blog/ArticleShareBar';
type ArticleBlock =
  | { kind: 'h2'; text: string }
  | { kind: 'h3'; text: string }
  | { kind: 'ul'; items: string[] }
  | { kind: 'p'; text: string }
  | { kind: 'html'; html: string };

function htmlToPlainText(input: string) {
  return input
    .replace(/<\s*br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|h1|h2|h3|h4|h5|h6|li|blockquote)>/gi, '\n\n')
    .replace(/<li>/gi, '- ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function parseNonTableArticleBlocks(raw: string): ArticleBlock[] {
  const normalized = htmlToPlainText(raw);
  const chunks = normalized
    .split(/\n\s*\n/)
    .map((chunk) => chunk.trim())
    .filter(Boolean);

  const blocks: ArticleBlock[] = [];

  for (const chunk of chunks) {
    if (chunk.startsWith('## ')) {
      blocks.push({ kind: 'h2', text: chunk.slice(3).trim() });
      continue;
    }
    if (chunk.startsWith('### ')) {
      blocks.push({ kind: 'h3', text: chunk.slice(4).trim() });
      continue;
    }

    const lines = chunk.split('\n').map((line) => line.trim()).filter(Boolean);
    if (lines.length > 1 && lines.every((line) => line.startsWith('- '))) {
      blocks.push({ kind: 'ul', items: lines.map((line) => line.slice(2).trim()) });
      continue;
    }

    blocks.push({ kind: 'p', text: chunk });
  }

  return blocks;
}

function parseArticleBlocks(raw: string): ArticleBlock[] {
  const blocks: ArticleBlock[] = [];
  const tablePattern = /<table[\s\S]*?<\/table>/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = tablePattern.exec(raw)) !== null) {
    const before = raw.slice(lastIndex, match.index).trim();
    if (before) blocks.push(...parseNonTableArticleBlocks(before));
    blocks.push({ kind: 'html', html: match[0] });
    lastIndex = match.index + match[0].length;
  }

  const tail = raw.slice(lastIndex).trim();
  if (tail) blocks.push(...parseNonTableArticleBlocks(tail));

  return blocks;
}

const BlogDetailPage = () => {
  const { lang, t } = useLanguage();
  const { slug } = useParams<{ slug: string }>();

  const { data: post, isLoading } = useQuery({
    queryKey: ['blog', 'detail', slug, lang],
    queryFn: () => fetchBlogPostBySlug(slug ?? ''),
    enabled: Boolean(slug),
  });

  useEffect(() => {
    if (slug) persistBlogLeadSlug(slug);
  }, [slug]);

  if (isLoading) {
    return <main className="pt-32 pb-20"><div className="container mx-auto px-6 lg:px-12 text-muted-foreground">{t('common.loading')}</div></main>;
  }

  if (!post) {
    return (
      <main className="pt-32 pb-20">
        <SEOHead title={t('blog.notFound')} description={t('blog.notFound')} canonical="/blog" noindex />
        <div className="container mx-auto px-6 lg:px-12">
          <h1 className="font-display text-4xl font-bold text-foreground mb-4">{t('blog.notFound')}</h1>
          <Link to="/blog" className="inline-flex items-center gap-2 font-body text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft size={16} />
            {t('blog.back')}
          </Link>
        </div>
      </main>
    );
  }

  const publishedDate = new Date(post.published_at || post.created_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const title = resolveLocalizedValue(post.title_i18n, post.title, lang);
  const excerpt = resolveLocalizedValue(post.excerpt_i18n, post.excerpt, lang);
  const category = resolveLocalizedValue(post.category_i18n, post.category, lang);
  const readingTime = resolveLocalizedValue(post.reading_time_i18n, '', lang);
  const localizedContent = resolveLocalizedValue(post.content_i18n, post.content, lang);
  const content = isMeaningfulBlogContent(localizedContent) ? localizedContent : '';
  const contentBlocks = parseArticleBlocks(content);
  const seoTitle = resolveLocalizedValue(post.seo_title_i18n, post.seo_title || title, lang);
  const seoDescription = resolveLocalizedValue(post.seo_description_i18n, post.seo_description || excerpt, lang);
  const publicBlogPath = getBlogLogicalPath(post, lang);
  const directAnswerText = resolveLocalizedValue(post.intro_i18n, excerpt, lang) || excerpt;
  const shareUrl = buildAbsoluteUrl(SITE_URL, localizePath(publicBlogPath, lang));
  const author = resolveAuthorProfile(post.author_name);
  const publishedIso = post.published_at || post.created_at;
  const coverImage = getBlogCoverImage(post);
  const publicBlogUrlPath = localizePath(publicBlogPath, lang);
  const schemaImage = buildAbsoluteUrl(SITE_URL, templateOgImagePath(publicBlogUrlPath));
  const authorProfileUrl = buildAbsoluteUrl(
    SITE_URL,
    localizePath(getAuthorProfilePath(author.slug), lang),
  );
  const relatedServices = getServicesBySlugs(
    getRelatedServiceSlugsForBlog({
      slug: post.slug,
      title,
      excerpt,
      tags: post.tags,
      category,
    }),
  );
  const conversion = getBlogConversionContext(post.slug);
  const relatedPlaybooks = getAuthorityAssetsBySlugs(conversion.playbookSlugs);

  const faqSchema = buildFaqSchema(
    conversion.faqs.map((item) => ({
      question: item.question[lang],
      answer: item.answer[lang],
    })),
    { canonicalPath: publicBlogPath, inLanguage: lang === 'tr' ? 'tr-TR' : 'en-US', locale: lang },
  );

  const blogPostingSchema = buildBlogPostingSchema({
    headline: seoTitle,
    description: seoDescription,
    path: localizePath(publicBlogPath, lang),
    datePublished: publishedIso,
    dateModified: post.published_at || post.created_at,
    image: schemaImage,
    inLanguage: lang === 'tr' ? 'tr-TR' : 'en-US',
    authorName: post.author_name,
  });

  const breadcrumbSchema = buildBreadcrumbSchema([
    { name: lang === 'tr' ? 'Ana Sayfa' : 'Home', item: SITE_URL },
    { name: lang === 'tr' ? 'Blog' : 'Blog', item: buildAbsoluteUrl(SITE_URL, localizePath('/blog', lang)) },
    { name: title, item: shareUrl },
  ]);

  const speakableSchema = buildSpeakableSchema({ canonicalPath: publicBlogPath, locale: lang });

  return (
    <main className="pt-32 pb-20 relative overflow-hidden">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        canonical={publicBlogPath}
        localeAlternatePaths={{
          en: `/blog/${post.slug}`,
          tr: `/blog/${getBlogPublicSlug(post.slug, 'tr')}`,
        }}
        type="article"
        authorName={author.name}
        publishedTime={publishedIso ?? undefined}
        modifiedTime={post.published_at || post.created_at || undefined}
        articleAuthorUrl={authorProfileUrl}
        jsonLd={[
          ...buildAuthorGraphNodes(post.author_name, lang),
          blogPostingSchema,
          breadcrumbSchema,
          faqSchema,
          speakableSchema,
        ]}
      />
      <FloatingAsset src={assetDiamond} size={130} className="top-36 -right-6 opacity-30" floatDuration={10} />

      <div className="container mx-auto px-6 lg:px-12 relative z-10">
        <Link to="/blog" className="inline-flex items-center gap-2 font-body text-sm text-muted-foreground hover:text-foreground transition-colors mb-10 group">
          <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
          {t('blog.back')}
        </Link>

        <motion.header
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mb-14"
        >
          <span className="inline-flex font-body text-[10px] tracking-[0.2em] text-muted-foreground uppercase bg-secondary px-3 py-1.5 rounded-full mb-6">
            {category}
          </span>
          <h1 className="font-display text-4xl lg:text-6xl font-bold text-foreground leading-tight mb-6">{title}</h1>
          <p className="font-body text-lg text-muted-foreground leading-relaxed mb-6">{excerpt}</p>
          <DirectAnswerBlock answer={directAnswerText} className="mb-6" />
          <div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-body text-xs text-muted-foreground">
              <time dateTime={publishedIso}>{publishedDate}</time>
              {readingTime ? (
                <>
                  <span aria-hidden>•</span>
                  <span>{readingTime}</span>
                </>
              ) : null}
              <span aria-hidden>•</span>
              <Link
                to={getAuthorProfilePath(author.slug)}
                className="hover:text-foreground transition-colors underline-offset-4 hover:underline"
              >
                {post.author_name}
              </Link>
            </div>
            {post.tags?.length ? (
              <div className="flex flex-wrap gap-2 mt-4">
                {uniqueBlogTags(post.tags).map((tag) => {
                  const tagPath = getBlogTagLinkPath(tag);
                  return tagPath ? (
                    <Link
                      key={tag}
                      to={tagPath}
                      className="font-body text-[10px] tracking-[0.12em] uppercase bg-secondary px-3 py-1.5 rounded-full text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {getBlogTagLabel(tag, lang)}
                    </Link>
                  ) : (
                    <span
                      key={tag}
                      className="font-body text-[10px] tracking-[0.12em] uppercase bg-secondary px-3 py-1.5 rounded-full text-muted-foreground"
                    >
                      {getBlogTagLabel(tag, lang)}
                    </span>
                  );
                })}
              </div>
            ) : null}
            <ArticleShareBar title={title} canonicalUrl={shareUrl} postSlug={post.slug} />
          </div>
        </motion.header>

        {coverImage ? (
          <motion.figure
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.05 }}
            className="max-w-5xl mb-14 overflow-hidden rounded-3xl border border-border/60 bg-secondary/50"
          >
            <img
              src={coverImage}
              alt={title}
              width={1280}
              height={800}
              loading="eager"
              decoding="async"
              className="block aspect-[16/10] h-auto w-full object-cover"
            />
          </motion.figure>
        ) : null}

        <motion.article
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="max-w-3xl space-y-6"
        >
          {contentBlocks.length === 0 ? (
            <p className="font-body text-muted-foreground">
              {lang === 'tr'
                ? 'Bu yazının içerik metni henüz yayımlanmadı. Lütfen daha sonra tekrar kontrol edin.'
                : 'The full body of this post is not published yet. Please check back later.'}
            </p>
          ) : null}
          {contentBlocks.map((block, index) => {
            if (block.kind === 'h2') {
              return (
                <h2 key={`${index}-h2`} className="font-display text-3xl font-bold text-foreground pt-4">
                  {block.text}
                </h2>
              );
            }
            if (block.kind === 'h3') {
              return (
                <h3 key={`${index}-h3`} className="font-display text-2xl font-semibold text-foreground pt-2">
                  {block.text}
                </h3>
              );
            }
            if (block.kind === 'ul') {
              return (
                <ul key={`${index}-ul`} className="list-disc pl-6 space-y-2 font-body text-lg leading-relaxed text-foreground/85">
                  {block.items.map((item, idx) => (
                    <li key={`${index}-${idx}`}>{item}</li>
                  ))}
                </ul>
              );
            }
            if (block.kind === 'html') {
              return (
                <div
                  key={`${index}-html`}
                  className="overflow-x-auto my-4"
                  dangerouslySetInnerHTML={{ __html: block.html }}
                />
              );
            }
            return (
              <p key={`${index}-p`} className="font-body text-lg leading-relaxed text-foreground/85">
                {block.text}
              </p>
            );
          })}
        </motion.article>

        <BlogConversionPanel
          blogSlug={post.slug}
          toolIds={conversion.toolIds}
          projectSlugs={conversion.projectSlugs}
        />

        <BlogLeadMagnet blogSlug={post.slug} />

        <div className="max-w-3xl">
          <FAQSection items={conversion.faqs} />
        </div>

        <RelatedServicesBlock services={relatedServices} />
        <RelatedPlaybooksBlock
          assets={relatedPlaybooks}
          title={lang === 'tr' ? 'Derinlemesine rehberler' : 'In-depth guides'}
        />
      </div>
    </main>
  );
};

export default BlogDetailPage;
